import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import { format, subDays } from 'date-fns'
import type { Profile } from '@/types/database'

interface OverviewStats {
  totalUsers: number
  activeSubscriptions: number
  trialUsers: number
  canceledUsers: number
  pastDueUsers: number
  mrr: number
  totalSites: number
  activeSites: number
  sessions30d: number
  events30d: number
  eventsToday: number
  scriptErrors24h: number
}

interface DbTableSize {
  table_name: string
  row_count: number
  total_size: string
}

export default function AdminOverviewPage() {
  const [stats, setStats] = useState<OverviewStats | null>(null)
  const [dbSize, setDbSize] = useState<DbTableSize[]>([])
  const [usersOverTime, setUsersOverTime] = useState<{ date: string; count: number }[]>([])
  const [eventsOverTime, setEventsOverTime] = useState<{ date: string; count: number }[]>([])
  const [topSites, setTopSites] = useState<{ domain: string; events: number }[]>([])
  const [recentErrors, setRecentErrors] = useState<{ error_type: string; error_count: number; last_occurrence: string }[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    setIsLoading(true)

    try {
      // Try to use the optimized admin function first
      const { data: overviewStats, error: overviewError } = await supabase
        .rpc('admin_get_overview_stats') as {
          data: {
            total_users: number
            active_subscriptions: number
            trial_users: number
            canceled_users: number
            past_due_users: number
            total_sites: number
            active_sites: number
            events_today: number
            events_30d: number
            sessions_30d: number
            script_errors_24h: number
          }[] | null
          error: Error | null
        }

      if (overviewStats && !overviewError && overviewStats.length > 0) {
        const s = overviewStats[0]
        setStats({
          totalUsers: Number(s.total_users) || 0,
          activeSubscriptions: Number(s.active_subscriptions) || 0,
          trialUsers: Number(s.trial_users) || 0,
          canceledUsers: Number(s.canceled_users) || 0,
          pastDueUsers: Number(s.past_due_users) || 0,
          mrr: (Number(s.active_subscriptions) || 0) * 10,
          totalSites: Number(s.total_sites) || 0,
          activeSites: Number(s.active_sites) || 0,
          sessions30d: Number(s.sessions_30d) || 0,
          events30d: Number(s.events_30d) || 0,
          eventsToday: Number(s.events_today) || 0,
          scriptErrors24h: Number(s.script_errors_24h) || 0,
        })
      } else {
        // Fallback to individual queries if function doesn't exist yet
        await fetchStatsLegacy()
      }

      // Get database size info
      const { data: dbSizeData } = await supabase.rpc('admin_get_db_size')
      if (dbSizeData) {
        setDbSize(dbSizeData)
      }

      // Get script error summary
      const { data: errorSummary } = await supabase.rpc('admin_get_script_error_summary')
      if (errorSummary) {
        setRecentErrors(errorSummary)
      }

      // Get users over time
      const { data: profiles } = await supabase.from('profiles').select('created_at') as { data: Pick<Profile, 'created_at'>[] | null }

      const usersByDay = profiles?.reduce((acc, p) => {
        const date = p.created_at.split('T')[0]
        acc[date] = (acc[date] || 0) + 1
        return acc
      }, {} as Record<string, number>) || {}

      const last30Days = Array.from({ length: 30 }, (_, i) => {
        const date = format(subDays(new Date(), 29 - i), 'yyyy-MM-dd')
        return { date, count: usersByDay[date] || 0 }
      })
      setUsersOverTime(last30Days)

      // Get daily stats for events over time
      const thirtyDaysAgo = subDays(new Date(), 30).toISOString()
      const { data: dailyStats } = await supabase
        .from('daily_stats')
        .select('date, total_events')
        .gte('date', thirtyDaysAgo.split('T')[0])
        .order('date', { ascending: true }) as { data: { date: string; total_events: number }[] | null }

      const eventsByDay = dailyStats?.reduce((acc, d) => {
        acc[d.date] = (acc[d.date] || 0) + d.total_events
        return acc
      }, {} as Record<string, number>) || {}

      const eventsLast30 = Array.from({ length: 30 }, (_, i) => {
        const date = format(subDays(new Date(), 29 - i), 'yyyy-MM-dd')
        return { date, count: eventsByDay[date] || 0 }
      })
      setEventsOverTime(eventsLast30)

      // Get top sites by events
      const { data: sites } = await supabase
        .from('sites')
        .select('domain, id') as { data: { domain: string; id: string }[] | null }

      if (sites && sites.length > 0) {
        const siteEventsPromises = sites.slice(0, 20).map(async (site) => {
          const { count } = await supabase
            .from('events')
            .select('*', { count: 'exact', head: true })
            .eq('site_id', site.id)
            .gte('timestamp', thirtyDaysAgo)
          return { domain: site.domain, events: count || 0 }
        })

        const siteEvents = await Promise.all(siteEventsPromises)
        setTopSites(siteEvents.sort((a, b) => b.events - a.events).slice(0, 10))
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Legacy fetch for when RPC functions don't exist
  const fetchStatsLegacy = async () => {
    const { data: profiles } = await supabase.from('profiles').select('subscription_status, created_at') as { data: Pick<Profile, 'subscription_status' | 'created_at'>[] | null }

    const statusCounts = profiles?.reduce((acc, p) => {
      acc[p.subscription_status] = (acc[p.subscription_status] || 0) + 1
      return acc
    }, {} as Record<string, number>) || {}

    const { count: sitesCount } = await supabase.from('sites').select('*', { count: 'exact', head: true })
    const { count: activeSitesCount } = await supabase.from('sites').select('*', { count: 'exact', head: true }).eq('is_active', true)

    const thirtyDaysAgo = subDays(new Date(), 30).toISOString()
    const { count: sessionsCount } = await supabase
      .from('sessions')
      .select('*', { count: 'exact', head: true })
      .gte('started_at', thirtyDaysAgo)

    const { count: eventsCount } = await supabase
      .from('events')
      .select('*', { count: 'exact', head: true })
      .gte('timestamp', thirtyDaysAgo)

    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    const { count: eventsTodayCount } = await supabase
      .from('events')
      .select('*', { count: 'exact', head: true })
      .gte('timestamp', todayStart.toISOString())

    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
    const { count: errorsCount } = await supabase
      .from('script_errors')
      .select('*', { count: 'exact', head: true })
      .gte('timestamp', yesterday)

    setStats({
      totalUsers: profiles?.length || 0,
      activeSubscriptions: statusCounts['active'] || 0,
      trialUsers: statusCounts['trialing'] || 0,
      canceledUsers: statusCounts['canceled'] || 0,
      pastDueUsers: statusCounts['past_due'] || 0,
      mrr: (statusCounts['active'] || 0) * 10,
      totalSites: sitesCount || 0,
      activeSites: activeSitesCount || 0,
      sessions30d: sessionsCount || 0,
      events30d: eventsCount || 0,
      eventsToday: eventsTodayCount || 0,
      scriptErrors24h: errorsCount || 0,
    })
  }

  const totalDbSize = dbSize.reduce((acc, t) => {
    const match = t.total_size.match(/(\d+(?:\.\d+)?)\s*(bytes|kB|MB|GB)/i)
    if (match) {
      const value = parseFloat(match[1])
      const unit = match[2].toLowerCase()
      const multiplier = unit === 'gb' ? 1024 * 1024 * 1024 :
        unit === 'mb' ? 1024 * 1024 :
        unit === 'kb' ? 1024 : 1
      return acc + value * multiplier
    }
    return acc
  }, 0)

  const formatBytes = (bytes: number) => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`
    return `${bytes} bytes`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Platform Overview</h1>

      {/* User & Subscription metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <MetricCard label="Total Users" value={stats?.totalUsers || 0} />
        <MetricCard label="Active Subs" value={stats?.activeSubscriptions || 0} color="green" />
        <MetricCard label="Trials" value={stats?.trialUsers || 0} color="yellow" />
        <MetricCard label="MRR" value={`€${stats?.mrr || 0}`} color="green" />
        <MetricCard label="Past Due" value={stats?.pastDueUsers || 0} color="red" />
      </div>

      {/* Site & Event metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Total Sites" value={stats?.totalSites || 0} />
        <MetricCard label="Active Sites" value={stats?.activeSites || 0} color="green" />
        <MetricCard label="Sessions (30d)" value={stats?.sessions30d || 0} />
        <MetricCard label="Events (30d)" value={stats?.events30d || 0} />
      </div>

      {/* System health metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <MetricCard label="Events Today" value={stats?.eventsToday || 0} />
        <MetricCard
          label="Script Errors (24h)"
          value={stats?.scriptErrors24h || 0}
          color={stats?.scriptErrors24h && stats.scriptErrors24h > 0 ? 'red' : undefined}
        />
        <MetricCard label="Database Size" value={formatBytes(totalDbSize)} />
        <MetricCard label="Tables" value={dbSize.length} />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">New Users (30 days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={usersOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => format(new Date(date), 'MMM d')}
                  stroke="#9ca3af"
                  fontSize={12}
                />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                  labelStyle={{ color: '#fff' }}
                  labelFormatter={(date) => format(new Date(date as string), 'MMM d, yyyy')}
                />
                <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Events (30 days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={eventsOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => format(new Date(date), 'MMM d')}
                  stroke="#9ca3af"
                  fontSize={12}
                />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                  labelStyle={{ color: '#fff' }}
                  labelFormatter={(date) => format(new Date(date as string), 'MMM d, yyyy')}
                />
                <Line type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Script Errors & Database Size */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Script Errors */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Script Errors (24h)</h3>
          {recentErrors.length > 0 ? (
            <div className="space-y-3">
              {recentErrors.map((error, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{error.error_type}</p>
                    <p className="text-gray-400 text-sm">
                      Last: {format(new Date(error.last_occurrence), 'MMM d, HH:mm')}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
                    {error.error_count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <span className="text-4xl">✅</span>
              <p className="text-gray-400 mt-2">No script errors in the last 24 hours</p>
            </div>
          )}
        </div>

        {/* Database Tables */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Database Tables</h3>
          {dbSize.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {dbSize.slice(0, 10).map((table, index) => (
                <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-700/30 rounded">
                  <span className="text-gray-300 font-mono text-sm">{table.table_name}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-sm">{table.row_count.toLocaleString()} rows</span>
                    <span className="text-gray-400 text-sm font-medium">{table.total_size}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">Unable to fetch database info</p>
          )}
        </div>
      </div>

      {/* Top sites */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Top 10 Sites by Events (30d)</h3>
        {topSites.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topSites} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                <YAxis
                  dataKey="domain"
                  type="category"
                  width={150}
                  stroke="#9ca3af"
                  fontSize={12}
                  tickFormatter={(domain) => domain.length > 20 ? domain.slice(0, 20) + '...' : domain}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="events" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No sites with events yet</p>
        )}
      </div>
    </div>
  )
}

function MetricCard({ label, value, color }: { label: string; value: string | number; color?: 'green' | 'yellow' | 'red' }) {
  const valueColor = color === 'green' ? 'text-green-400' :
    color === 'yellow' ? 'text-yellow-400' :
    color === 'red' ? 'text-red-400' :
    'text-white'

  return (
    <div className="bg-gray-800 rounded-xl p-4">
      <p className="text-sm text-gray-400">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valueColor}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  )
}
